import { keepPreviousData, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete } from 'src/components/Autocomplete';
import { CircularProgress } from 'src/components/CircularProgress';
import { TextField } from 'src/components/TextField';
import { Theme } from 'src/components/muiTheme/utils';
import { SxProps } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import { Exercise } from 'src/models/exercise/_types';
import { searchExercises } from 'src/models/exercise/exercise';

interface ExerciseAutocompleteProps {
  value: Exercise | null;
  onChange: (exercise: Exercise | null) => void;
  label?: string;
  placeholder?: string;
  sx?: SxProps<Theme>;
}

export const ExerciseAutocomplete: React.FC<ExerciseAutocompleteProps> = ({
  value,
  onChange,
  label = 'Select Exercise',
  placeholder = '',
  sx,
}) => {
  const { session } = useSession();
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<Exercise[]>([]);

  const {
    data: exercises,
    refetch: refetchExercises,
    isFetching,
  } = useQuery({
    queryKey: ['exercises', 'search', searchValue],

    queryFn: ({ signal }) =>
      searchExercises({
        search: searchValue,
        trainerId: session.trainerId,
        signal,
      }),

    enabled: false,
    placeholderData: keepPreviousData,
    staleTime: 5000,
  });

  useEffect(() => {
    if (exercises) {
      setOptions(exercises);
    }
  }, [exercises]);

  const debouncedSearch = useMemo(() => {
    return debounce((value: string) => {
      if (value.length >= 2) {
        refetchExercises();
      }
    }, 300);
  }, [refetchExercises]);

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, value: string, reason: string) => {
      if (reason === 'input') {
        setSearchValue(value);
        debouncedSearch(value);
      }
    },
    [debouncedSearch]
  );

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option: Exercise) => option.name}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={handleInputChange}
      loading={isFetching}
      filterOptions={(x) => x}
      noOptionsText={
        searchValue ? 'No exercises found' : 'Start typing to find exercises'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? (
                  <CircularProgress color="inherit" size="small" />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={sx}
    />
  );
};
